import React from 'react'

import {Action} from '../../../commons/src/types'
import {
  ContactModalOption,
  DefaultProductionTimes,
  Discount,
  DiscountType,
  DynamicProductionTime,
  DynamicProductionTimeId,
  FinishGroupId,
  MaterialGroupId,
  ModalConfigOpened,
  ModalContentType,
  Option,
  OptionWithLinkedProviderInfos,
  Price,
  PriceId,
  ProviderEmail,
  ProviderId,
  ProviderInfo,
  Shipping,
} from '../types'

import {Order, OrderRefund} from '../order-types'

export type OpenModalAction = Action<'MODAL.OPEN', ModalConfigOpened>
export type CloseModalAction = Action<'MODAL.CLOSE', void>
export type ModalAction = OpenModalAction | CloseModalAction

const open = (config: ModalConfigOpened): OpenModalAction => ({
  type: 'MODAL.OPEN',
  payload: config,
})

export const close = (): CloseModalAction => ({
  type: 'MODAL.CLOSE',
  payload: undefined,
})

export const openFatalErrorModal = (error: Error) =>
  open({
    isCloseable: false,
    contentType: ModalContentType.FATAL_ERROR,
    contentProps: {
      error,
    },
  })

export const openChangePasswordModal = () =>
  open({
    isCloseable: true,
    contentType: ModalContentType.AUTH_CHANGE_PASSWORD,
    contentProps: {},
  })

export const openCreatePricingModal = (onDone?: (price: Price) => void) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CREATE_PRICING,
    contentProps: {onDone},
  })

export const openCreateDynamicProductionTimeModal = (
  onDone?: (dynamicProductionTime: DynamicProductionTime) => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CREATE_DYNAMIC_PRODUCTION_TIME,
    contentProps: {onDone},
  })

export const openEditDiscountsModal = (
  type: DiscountType,
  discounts: Discount[] = [],
  onDone?: () => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.EDIT_DISCOUNTS,
    contentProps: {type, discounts, onDone},
  })

export const openCreateShippingsModal = (onDone?: () => void) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CREATE_SHIPPINGS,
    contentProps: {onDone},
  })

export const openEditShippingsModal = (shippings: Shipping[], onDone?: () => void) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.EDIT_SHIPPINGS,
    contentProps: {shippings, onDone},
  })

export const openCreateInfillModal = (onDone?: (infill: Option) => void) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CREATE_INFILL,
    contentProps: {onDone},
  })

export const openEditInfillsModal = (
  infills: OptionWithLinkedProviderInfos[],
  onDone?: () => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.EDIT_INFILLS,
    contentProps: {infills, onDone},
  })

export const openCreateProductionTimeModal = (
  linkedConfigsCount?: number,
  defaultProductionTimes?: DefaultProductionTimes,
  onDone?: (productionTime: Option) => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CREATE_PRODUCTION_TIME,
    contentProps: {defaultProductionTimes, linkedConfigsCount, onDone},
  })

export const openEditProductionTimesModal = (
  productionTimeOptions: OptionWithLinkedProviderInfos[],
  linkedConfigsCount: number,
  defaultProductionTimes?: DefaultProductionTimes,
  onDone?: () => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.EDIT_PRODUCTION_TIMES,
    contentProps: {defaultProductionTimes, linkedConfigsCount, productionTimeOptions, onDone},
  })

export const openMaterialModal = (materialId: MaterialGroupId) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.MATERIAL,
    contentProps: {materialId},
  })

export const openFinishGroupModal = (finishGroupId: FinishGroupId) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.FINISH_GROUP,
    contentProps: {finishGroupId},
  })

export const openEditProviderEmailsModal = (providerEmails: ProviderEmail[], onDone?: () => void) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.EDIT_PROVIDER_EMAILS,
    contentProps: {providerEmails, onDone},
  })

export const openConfirmDeleteModal = (
  itemsToDeleteCount: number,
  onConfirm: () => void,
  linkedConfigsCount?: number
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CONFIRM_DELETE,
    contentProps: {itemsToDeleteCount, linkedConfigsCount, onConfirm},
  })

export const openConfirmToggleAvailabilityModal = (enable: boolean, onConfirm: () => void) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CONFIRM_TOGGLE_AVAILABILITY,
    contentProps: {enable, onConfirm},
  })

export const openAddProviderInfoDimensionsModal = (
  providerInfos: ProviderInfo[],
  onDone?: () => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_DIMENSIONS,
    contentProps: {providerInfos, onDone},
  })

export const openAddProviderInfoProductionTimesModal = (
  providerId: ProviderId,
  providerInfos: ProviderInfo[],
  onDone?: () => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_PRODUCTION_TIMES,
    contentProps: {providerId, providerInfos, onDone},
  })

export const reopenAddProviderInfoProductionTimesModal = (
  providerId: ProviderId,
  providerInfos: ProviderInfo[],
  prefilledInitialValues?: {
    productionTimeFast: string
    productionTimeSlow: string
    productionTimeIds: string[]
  },
  onDone?: () => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_PRODUCTION_TIMES,
    contentProps: {providerId, providerInfos, prefilledInitialValues, onDone},
  })

export const openAddProviderInfoPricingModal = (
  providerId: ProviderId,
  providerInfos: ProviderInfo[],
  onDone?: () => void,
  initialPriceId?: PriceId
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_PRICING,
    contentProps: {providerId, providerInfos, initialPriceId, onDone},
  })

export const openAddDynamicProductionTimeModal = (
  providerId: ProviderId,
  providerInfos: ProviderInfo[],
  onDone?: () => void,
  initialDynamicProductionTimeId?: DynamicProductionTimeId
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_DYNAMIC_PRODUCTION_TIME,
    contentProps: {providerId, providerInfos, initialDynamicProductionTimeId, onDone},
  })

export const openAddProviderInfoPrintingMethodModal = (
  providerInfos: ProviderInfo[],
  onDone?: () => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.ADD_PROVIDER_INFO_PRINTING_METHOD,
    contentProps: {providerInfos, onDone},
  })

export const openProviderInfoSuggestImmediateEditingModal = (
  count: number,
  onConfirm: () => void,
  onCancel: () => void
) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.PROVIDER_INFO_SUGGEST_IMMEDIATE_EDITING,
    contentProps: {count, onConfirm, onCancel},
  })

export const openContactModal = (contactOptions?: ContactModalOption[]) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CONTACT,
    contentProps: {
      contactOptions,
    },
  })

export const openContactFormModal = () =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CONTACT_FORM,
    contentProps: {},
  })

export const openConfirmDeleteMessagesModal = (onConfirm: () => void) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.CONFIRM_DELETE_MESSAGE,
    contentProps: {onConfirm},
  })

export const openSimpleContentModal = (title: string, content: React.ReactNode, warning = false) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.SIMPLE_CONTENT,
    contentProps: {title, content, warning},
  })

export const openSimpleConfirmModal = (
  title: string,
  content: React.ReactNode,
  onConfirm: () => void,
  confirmButtonLabel?: string
) =>
  open({
    isCloseable: false,
    contentType: ModalContentType.SIMPLE_CONFIRM,
    contentProps: {title, content, onConfirm, confirmButtonLabel},
  })

export const openAdditionalCostModal = (
  order: Order,
  providerId: ProviderId,
  onDone: () => Promise<void>
) =>
  open({
    isCloseable: false,
    contentType: ModalContentType.ADDITIONAL_COST,
    contentProps: {order, providerId, onDone},
  })

export const openLoadingModal = (message: string) =>
  open({
    isCloseable: false,
    contentType: ModalContentType.LOADING,
    contentProps: {
      message,
    },
  })

export const openAddTrackingNumberModal = (
  order: Order,
  onDone: (trackingNumber?: string) => void
) =>
  open({
    isCloseable: false,
    contentType: ModalContentType.ENFORCE_TRACKING,
    contentProps: {
      order,
      onDone,
    },
  })

export const openRequestRefundModal = (
  order: Order,
  providerId: ProviderId,
  onConfirm: () => void
) =>
  open({
    isCloseable: false,
    contentType: ModalContentType.REQUEST_REFUND,
    contentProps: {order, providerId, onConfirm},
  })

export const openPartialRefundDetailsModal = (order: Order, refund: OrderRefund) =>
  open({
    isCloseable: true,
    contentType: ModalContentType.PARTIAL_REFUND_DETAILS,
    contentProps: {order, refund},
  })
